import type { EmployeeSnapshot } from '@klukka/client';
import { buildTimesheet, fmt, fmtDate, fmtDay, fmtH, t, type LangCode } from '@klukka/core';
import { Ic, StatusChip, Trail } from '@klukka/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { EmployeeView } from '../derive';
import { api } from '../api';
import { PeriodNav } from '../PeriodNav';

/** Worklog report: hours, vacation accrual, adjustments, payslips, and signing. */
export function ReportTab({ snap, view, lang, offset, onOffset, onSign, highlightSlip }: {
  snap: EmployeeSnapshot;
  view: EmployeeView;
  lang: LangCode;
  offset: number;
  onOffset: (n: number) => void;
  onSign: () => void;
  /** Period key from an emailed link, scrolled to and marked on arrival. */
  highlightSlip?: string | null;
}) {
  const tr = (k: Parameters<typeof t>[1]) => t(lang, k);
  const [orlofTip, setOrlofTip] = useState(false);
  const { sel, workedHours, expected, sickDays, orlof } = view;

  const signedForPeriod = snap.timesheets.find((w) => w.periodKey === sel.key && w.status !== 'rejected');
  const periodAdjustments = snap.adjustments.filter((a) => a.date >= sel.pinfo.start && a.date <= sel.pinfo.end);

  return (
    <div className="ph-body">
      <PeriodNav pinfo={sel.pinfo} offset={offset} onOffset={onOffset} />

      <div className="card">
        <div className="card-title">
          {tr('worklog')} {tr('forPeriod')} {fmtDay(sel.pinfo.start)}–{fmtDay(sel.pinfo.end)}
        </div>
        <div className="rowline hero"><span>{tr('nextPayout')}</span><b style={{ color: 'var(--primary)' }}>{fmtDate(view.payout.sel)}</b></div>
        <div className="rowline top-sep">
          <span>{tr('total')}</span>
          <b>{fmtH(workedHours)} {tr('hours')} / {sel.calc.worked} {tr('days')}</b>
        </div>
        <div className="rowline dim">
          <span>{tr('expectedWork')}</span>
          <b>{expected.days} {tr('days')} · {fmtH(expected.hours)} {tr('hours')}</b>
        </div>
        <div className="rowline"><span>{tr('sickDays')}</span><b>{sickDays}</b></div>

        {/*
          A statement, not a snapshot. The orlofsár runs 1 May – 30 April, so
          it is labelled 2026/27 rather than by calendar year, and the three
          lines add up in front of the reader: what carried over, what has been
          earned since, what has been taken. Stepping back a period shows that
          period's statement — the balance never shrinks just because the
          screen moved forward, which is what it used to do.
        */}
        <div className="rowline top-sep">
          <span className="fld-head" style={{ color: 'inherit' }}>
            {tr('orlofDays')} {orlof.label}
            <button type="button" className="tip-btn" onClick={() => setOrlofTip(!orlofTip)} aria-label={tr('moreInfo')}>
              <Ic.info size={15} />
            </button>
          </span>
          <b>{fmtH(orlof.accruedDays)} {tr('days')}</b>
        </div>
        {orlofTip ? (
          <span className="tooltip" style={{ margin: '4px 0 8px' }}>
            {tr('orlofTip')}
            <a href="https://island.is/orlofsrettur" target="_blank" rel="noreferrer noopener">{tr('moreInfo')} ↗</a>
          </span>
        ) : null}
        <div className="rowline dim"><span>+ {tr('orlofBrought')}</span><b>{fmtH(orlof.openingDays)} {tr('days')}</b></div>
        <div className="rowline dim"><span>− {tr('orlofTaken')}</span><b>{fmtH(orlof.takenDays)} {tr('days')}</b></div>
        <div className="rowline hero"><span>{tr('orlofBalance')}</span><b style={{ color: 'var(--primary)' }}>{fmtH(orlof.balanceDays)} {tr('days')}</b></div>
        <div className="rowline dim"><span>{tr('orlofAccrued')} ({tr('forPeriod')})</span><b>{fmt(sel.calc.orlof)}</b></div>
      </div>

      <WorkSheet snap={snap} lang={lang} periodStart={sel.pinfo.start} periodEnd={sel.pinfo.end} />

      {!signedForPeriod ? (
        <div className="grant-banner" style={{ borderColor: 'var(--warn)', background: '#f7efe2', color: 'var(--warn)' }}>
          <Ic.alert size={15} /> {tr('signRequired')}
        </div>
      ) : null}

      <Payslips snap={snap} lang={lang} highlight={highlightSlip} />

      {periodAdjustments.length > 0 ? (
        <div className="card">
          <div className="card-title">{tr('adjustments')}</div>
          {periodAdjustments.map((a) => (
            <div className="rowline" key={a.id}>
              <span>{fmtDay(a.date)}{a.note ? ` · ${a.note}` : ''}</span>
              <b style={{ color: a.amount >= 0 ? 'var(--ok)' : 'var(--danger)' }}>
                {a.amount >= 0 ? '+' : ''}{fmt(a.amount)}
              </b>
            </div>
          ))}
          <div className="dimtx" style={{ fontSize: 12, marginTop: 6 }}>{tr('adjInfo')}</div>
        </div>
      ) : null}

      {!signedForPeriod ? (
        <button className="btn-main" onClick={onSign}>
          <Ic.sign size={17} /> {tr('submitWeek')}
        </button>
      ) : null}

      {snap.timesheets.map((w) => (
        <div className="card" key={w.id}>
          <div className="rowline">
            <span><b>{w.label}</b> · {fmtH(w.hours)} {tr('hours')}</span>
            <StatusChip status={w.status} label={tr(w.status as Parameters<typeof t>[1])} />
          </div>
          <Trail trail={w.trail} />
        </div>
      ))}
    </div>
  );
}

/**
 * The payslips this employee can download.
 *
 * One per approved period, and each is rendered on demand rather than fetched
 * from a store — so what downloads here is the same calculation the employer
 * sees, not a copy taken at approval time.
 */
function Payslips({ snap, lang, highlight }: {
  snap: EmployeeSnapshot;
  lang: LangCode;
  highlight?: string | null;
}) {
  const tr = (k: Parameters<typeof t>[1]) => t(lang, k);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const target = useRef<HTMLDivElement | null>(null);

  // An emailed link lands on this tab; scroll the named period into view so the
  // employee sees the thing they were told about rather than the top of a page.
  useEffect(() => {
    if (highlight && target.current) {
      target.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlight]);

  const slips = snap.payslips;

  const download = async (periodKey: string) => {
    setBusy(periodKey);
    setErr(null);
    try {
      const bytes = await api.myPayslipPdf(periodKey);
      const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `launasedill-${periodKey.slice(0, 10)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card">
      <div className="card-title">{tr('payslips')}</div>

      {slips.length === 0 ? (
        <>
          <div className="rowline dim"><span>{tr('noPayslips')}</span></div>
          <div className="dimtx" style={{ fontSize: 12, marginTop: 6 }}>{tr('payslipsNote')}</div>
        </>
      ) : (
        <>
          {slips.map((p) => (
            <div
              key={p.periodKey}
              ref={p.periodKey === highlight ? target : undefined}
              className="rowline"
              style={p.periodKey === highlight
                ? { background: 'var(--tint)', borderRadius: 10, padding: '8px 10px', margin: '0 -10px' }
                : undefined}
            >
              <span>
                {fmtDay(p.periodStart)}–{fmtDay(p.periodEnd)}
                <span className="dimtx" style={{ display: 'block', fontSize: 11 }}>
                  {tr('nextPayout')} {fmtDate(p.payoutDate)}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <b style={{ color: 'var(--primary)' }}>{fmt(p.net)}</b>
                <button
                  className="btn-sm"
                  disabled={busy !== null}
                  onClick={() => void download(p.periodKey)}
                  aria-label={`${tr('downloadPdf')} ${p.periodStart}`}
                >
                  <Ic.download size={14} /> {busy === p.periodKey ? '…' : tr('downloadPdf')}
                </button>
              </span>
            </div>
          ))}
          {err ? (
            <div className="dimtx" style={{ fontSize: 12, marginTop: 6, color: 'var(--danger)' }}>{err}</div>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * The sheet itself — every day of the period, as recorded.
 *
 * It sits directly above the signature button, because signing a timesheet you
 * have not been shown is not much of a signature. Collapsed by default so the
 * summary above stays the first thing on the screen, and it lists the empty
 * days too: a gap is worth noticing before you put your name to the month.
 */
function WorkSheet({ snap, lang, periodStart, periodEnd }: {
  snap: EmployeeSnapshot;
  lang: LangCode;
  periodStart: string;
  periodEnd: string;
}) {
  const tr = (k: Parameters<typeof t>[1]) => t(lang, k);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doc = useMemo(() => buildTimesheet({
    employee: snap.employee,
    settings: snap.company.settings,
    period: { start: periodStart, end: periodEnd },
    timesheets: snap.timesheets,
    edits: snap.edits,
    today: snap.serverToday,
  }), [snap, periodStart, periodEnd]);

  const download = async () => {
    setBusy(true);
    setErr(null);
    try {
      const bytes = await api.myTimesheetPdf({ periodStart, periodEnd });
      const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vinnuskyrsla-${periodStart}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const dows = ['sun', 'mán', 'þri', 'mið', 'fim', 'fös', 'lau'];

  return (
    <div className="card">
      <div className="card-title">{tr('workSheet')}</div>

      <button className="btn-sm" onClick={() => setOpen(!open)} style={{ marginBottom: open ? 10 : 0 }}>
        <Ic.doc size={14} /> {open ? tr('hideSheet') : tr('viewSheet')}
      </button>

      {open ? (
        <>
          {doc.days.map((d) => {
            const note = d.sick ? tr('sickDays')
              : d.vacation ? tr('orlofDays')
                : d.holiday ? tr('holidayDay')
                  : d.empty ? (d.weekend ? tr('weekendDay') : tr('notRecordedDay'))
                    : d.edited ? tr('editedDay') : '';
            const worked = d.dvH + d.yv1H + d.yv2H;
            return (
              <div className="rowline" key={d.date} style={d.empty ? { color: 'var(--muted)' } : undefined}>
                <span>
                  <b>{Number(d.date.slice(8))}.{Number(d.date.slice(5, 7))}.</b>{' '}
                  <span className="dimtx">{dows[d.dow]}</span>
                  {note ? <span className="dimtx" style={{ marginLeft: 8, fontSize: 11 }}>{note}</span> : null}
                </span>
                <b>{worked > 0 ? `${fmtH(worked)} ${tr('hours')}` : '—'}</b>
              </div>
            );
          })}
          <div className="rowline top-sep">
            <span>{tr('total')}</span>
            <b>{fmtH(doc.totals.hours)} {tr('hours')} / {doc.totals.daysWorked} {tr('days')}</b>
          </div>

          <button className="btn-sm" disabled={busy} onClick={() => void download()} style={{ marginTop: 10 }}>
            <Ic.download size={14} /> {busy ? '…' : tr('downloadSheet')}
          </button>
          {err ? (
            <div className="dimtx" style={{ fontSize: 12, marginTop: 6, color: 'var(--danger)' }}>{err}</div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
